import handleTransaction from "helpers/handleTransaction";
import { NFTs } from "./../eth/contracts/contracts";
import { action, autorun, observable, runInAction } from "mobx";
import EthStore from "./EthStore";
import { Contract } from "ethers";
import { getContractByName } from "eth/contracts/contracts";
import { NFTInfo, NFTNames } from "types";
import { ensure } from "helpers/utils";
import VaultStore from "./VaultStore";

type NFTApproval = {
  tokenId: number;
  hasApproval: boolean;
};

const NFTContractIDToName = (id: number) => {
  if (id === 0) {
    return "RAM1";
  }
  if (id === 1) {
    return "RAM2";
  }
  if (id === 2) {
    return "RAM3";
  }
  if (id === 3) {
    return "RAM4";
  }
  if (id === 4) {
    return "RAM5";
  }
  if (id === 5) {
    return "ROBOT";
  }
  if (id === 6) {
    return "LINK";
  }
};

class NFTStore {
  @observable userNFTs: Map<NFTNames, NFTInfo[]> = new Map();
  @observable nftApprovals: Map<NFTNames, NFTApproval[]> = new Map();
  @observable nftsInUse: Map<NFTNames, boolean> = new Map();
  @observable unclaimedLink = 0;
  @observable unclaimedRobot = 0;

  NFTFactory: Contract | any = null;
  @observable NFTContracts: Map<NFTNames, Contract> = new Map();

  constructor() {
    autorun(this.syncToStorage);
  }

  syncToStorage = () => {
    if (localStorage) {
      localStorage.setItem("ygy-user-nfts", JSON.stringify(this.userNFTs));
      localStorage.setItem("ygy-nft-approvals", JSON.stringify(this.nftApprovals));
    }
  };

  // Store initializer
  @action initialize = async (network: string) => {
    NFTs[network].map((contract) => this.NFTContracts.set(contract.name, new Contract(contract.address, contract.abi, EthStore.signer)));
    const contract = getContractByName("NFTFactory", network);
    this.NFTFactory = new Contract(contract.address, contract.abi, EthStore.signer);
    this.NFTFactory.on("NFTMinted", this.setNFT);
    await this.fetchNFTBalances();
    await this.getUnclaimed();
    await this.nftInUse();
  };

  // Base data fetch function.
  fetchTokenData = async (contract, tokenId): Promise<NFTInfo> => ({
    contractId: Number(await contract.contractId()),
    tokenId: Number(tokenId),
    properties: await contract.properties(tokenId),
  });

  // Fetch unclaimed ROBOT / LINK nft amounts
  @action getUnclaimed = async () => {
    const routerContract = getContractByName("Router", EthStore.network);

    const router = new Contract(routerContract.address, routerContract.abi, EthStore.provider);

    const unclaimedLink = Number(await router.linkNFTsToBeClaimed(EthStore.account));
    const unclaimedRobot = Number(await router.robotNFTsToBeClaimed(EthStore.account));

    runInAction(() => {
      this.unclaimedLink = unclaimedLink;
      this.unclaimedRobot = unclaimedRobot;
    });
  };
  // Set an nft from the event listener.
  @action setNFT = async (name: NFTNames, to: string, tokenId: number) => {
    if (to === EthStore.account) {
      const contract = this.NFTContracts.get(name);
      if (contract) {
        let nfts: NFTInfo[] | undefined = [];
        nfts = this.userNFTs.get(name);
        const result = await this.fetchTokenData(contract, tokenId);
        if (nfts && nfts.find((nft) => nft.tokenId === result.tokenId)) return;
        if (!nfts) {
          nfts = [result];
        } else {
          if (nfts.find((nft) => nft.tokenId !== result.tokenId)) {
            nfts.push(result);
          }
        }
        runInAction(() => this.userNFTs.set(name, nfts!));
      }
    }
  };

  @action nftInUse = async () => {
    const nftInUse = await VaultStore.storage.getNFTsInUse(EthStore.account);
    if (nftInUse && nftInUse.length) {
      nftInUse.forEach((item) => {
        const itemName = NFTContractIDToName(Number(item.contractId));
        if (itemName) {
          runInAction(() => this.nftsInUse.set(itemName, true));
        }
      });
    }
  };

  @action useNFT = async (NFTName: NFTNames, tokenId: number, closeModal) => {
    const SingleNFTContract = ensure(this.NFTContracts.get(NFTName));
    await handleTransaction(
      async () => await this.NFTFactory.useNFT(SingleNFTContract.address.toString(), tokenId, 0),
      `usage of ${NFTName} NFT`
    );
    runInAction(() => this.userNFTs.set(NFTName, []));
    await this.fetchNFTBalances();
    await VaultStore.updateCallback("pool-base");
    closeModal();
  };

  @action approve = async (NFTName: NFTNames, tokenId: number) => {
    const contract = ensure(this.NFTContracts.get(NFTName));
    await handleTransaction(async () => await contract.approve(this.NFTFactory.address, tokenId), `NFT approval`);
    let oldApprovals = this.nftApprovals.get(NFTName);
    if (!oldApprovals) {
      oldApprovals = [{ tokenId, hasApproval: true }];
    } else {
      oldApprovals.push({ tokenId, hasApproval: true });
    }

    runInAction(() => this.nftApprovals.set(NFTName, oldApprovals!));
    return true;
  };

  getApproved = async (name: NFTNames, tokenId: number) => {
    const contract = ensure(this.NFTContracts.get(name));
    const approved = await contract.getApproved(tokenId);
    return approved.toLowerCase() === this.NFTFactory.address.toLowerCase();
  };

  // Damn..
  @action fetchNFTBalances = async () => {
    const arrr = [...this.NFTContracts.entries()];
    await Promise.all(
      arrr.map(async ([name, contract]) => {
        let nftsOwned = this.userNFTs.get(name as NFTNames);
        const balance = Number(await contract.balanceOf(EthStore.account));
        if (balance > 0) {
          const totalSupply = Number(await contract.totalSupply());
          if (totalSupply === 0) return;
          for (let i = 0; i < totalSupply; i++) {
            const tokenId = await contract.tokenOfOwnerByIndex(EthStore.account, i);
            if (tokenId) {
              const result = await this.fetchTokenData(contract, tokenId);
              if (nftsOwned && nftsOwned.find((nft) => nft.tokenId === result.tokenId)) return;
              if (!nftsOwned) {
                nftsOwned = [result];
              } else {
                nftsOwned.push(result);
              }
            }
          }
          runInAction(() => this.userNFTs.set(name as NFTNames, nftsOwned || []));
          return;
        }
      })
    );
    return;
  };
}

export default new NFTStore();
